import { ensureValue, uploadFiles } from '@rematter/ui-web';
import { atom } from 'jotai';

import { confirmFileUploadAtom } from '../_graphql/mutationFileConfirmUpload';
import { loadFileCreateAtom } from '../_graphql/mutationFileCreate';
import { fileDeleteAtom } from '../_graphql/mutationFileDelete';
import { loadMarkAsReceivedAtom } from '../_graphql/mutationLoadMarkAsReceived';
import { loadMarkAsReviewedAtom } from '../_graphql/mutationLoadMarkAsReviewed';
import { loadCustomerFilesQueryAtom } from '../_graphql/queryLoadCustomerFiles';
import { loadDetailsQueryAtom } from '../_graphql/queryLoadDetails';
import { loadMaterialPhotosQueryAtom } from '../_graphql/queryLoadMaterialPhotos';
import { loadPhotosQueryAtom } from '../_graphql/queryLoadPhotos';
import { customerPortalRoutes } from '../routes';
import { EntityLoadDetailsTab, LoadDetailsTab } from '../types';

import { filesWithUrlsAtom } from './files.state';
import { $remoteLoads } from './loadList.state';

import { $user } from '~/shared/providers/auth/identity.localState';
import { atomFromQueryString, atomFromRoutePath } from '~/shared/state';

// 🟣🟣🟣 PARAMS
const $loadDetailsParams = atomFromRoutePath(customerPortalRoutes.loadDetails);
export const $entityLoadId = atomFromQueryString.string<string>('loadId');
export const $loadId = atom(
  (get) => get($loadDetailsParams)?.loadId || get($entityLoadId),
);
// On booking details and purchase order details, there's a loadTab
export const $loadTab =
  atomFromQueryString.string<EntityLoadDetailsTab>('loadTab');
export const $tab = atomFromQueryString.string<LoadDetailsTab>('tab');

// 🔘🔘🔘 PLAIN ATOMS
const $uploadProgress = atom({ isUploading: false, progress: 0 });

// 🟢🟢🟢 COMPUTED DATA -> upload progress
export const $getUploadProgress = atom((get) => get($uploadProgress));

// 🟢🟢🟢 REMOTE DATA
export const $loadDetails = loadDetailsQueryAtom((get) => ({
  variables: { id: get($loadId) },
  skip: !get($loadId),
}));

// 🟢🟢🟢 REMOTE DATA -> load customer files
export const $loadCustomerFiles = loadCustomerFilesQueryAtom((get) => ({
  variables: { id: get($loadId) },
  skip: !get($loadId),
}));

// 🟢🟢🟢 REMOTE DATA -> load material photos
const $loadMaterialPhotosWithoutUrl = loadMaterialPhotosQueryAtom((get) => ({
  variables: { id: get($loadId) },
  lazy: true,
}));

// 🟢🟢🟢 COMPUTED DATA -> load material photos with urls
export const $loadMaterialPhotos = filesWithUrlsAtom((get) => ({
  variables: { id: get($loadId) },
  skip: !get($loadId),
  photosAtom: $loadMaterialPhotosWithoutUrl,
}));

// 🟢🟢🟢 REMOTE DATA -> load photos without url
const $loadPhotosWithoutUrl = loadPhotosQueryAtom((get) => ({
  variables: { id: get($loadId) },
  lazy: true,
}));

// 🟢🟢🟢 COMPUTED DATA -> load photos
export const $loadPhotos = filesWithUrlsAtom((get) => ({
  variables: { id: get($loadId) },
  skip: !get($loadId),
  photosAtom: $loadPhotosWithoutUrl,
}));

// 🟢🟢🟢 REMOTE DATA -> is loading photos
export const $isLoadingPhotos = atom((get) => {
  const loadPhotos = get($loadPhotos);
  const loadMaterialPhotos = get($loadMaterialPhotos);

  return loadPhotos.isLoading || loadMaterialPhotos.isLoading;
});

// 🟡🟡🟡 HIDDEN ACTION -> MARK remote load as reviewed
export const $markLoadAsReviewed = loadMarkAsReviewedAtom({
  onSuccess: (get) => get($loadDetails).refetch(),
});

// 🟠🟠🟠 ACTION -> MARK load as received
export const $markLoadAsReceived = loadMarkAsReceivedAtom({
  onSuccess: (get) => {
    get($loadDetails).refetch();
    get($remoteLoads).refetch();
  },
});

// 🟠🟠🟠 ACTION -> CREATE file
export const $createLoadFile = loadFileCreateAtom();

// 🟠🟠🟠 ACTION -> CONFIRM upload
export const $loadConfirmUpload = confirmFileUploadAtom({
  onSuccess: (get) => get($loadDetails).refetch(),
});

// 🟠🟠🟠 ACTION -> DELETE file
export const $deleteLoadFile = fileDeleteAtom({
  onSuccess: (get) => get($loadDetails).refetch(),
});

// 🟠🟠🟠 ACTION -> ADD load attachments
export const $addLoadAttachments = atom(
  null,
  async (get, set, files: File[]) => {
    const user = get($user);

    set($uploadProgress, { isUploading: true, progress: 0 });

    try {
      await uploadFiles(files, {
        userId: ensureValue(user?.id),
        onProgress: (progress) => {
          set($uploadProgress, { isUploading: true, progress });
        },
        onFinish: () => get($loadDetails).refetch(),
        createFile: (params) =>
          set($createLoadFile, {
            ...params,
            loadId: ensureValue(get($loadId)),
          }),
        confirmUpload: (params) => set($loadConfirmUpload, params),
      });
    } finally {
      // Reset upload progress
      set($uploadProgress, { isUploading: false, progress: 0 });
    }
  },
);
